import { SnackBarService } from '@app-core/services/snackbar/snack-bar.service';
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { AccessService } from '@app-core/services/access/access.service';
import { Observable } from 'rxjs';

import { getClientConfig } from '@config/client-config';
import { internalRoutes } from '@config/config.base';
import { DataService } from '@app-core/services/data/data.service';
import { navigationRoutes } from '@config/links.base';
import { TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs/operators';
import { DurationRangeService } from '@app-core/services/duration-range/duration-range.service';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard implements CanActivate {
  public clientConfig = getClientConfig().configuration;
  constructor(
    private accessService: AccessService,
    private router: Router,
    private dataService: DataService,
    private snackBarService: SnackBarService,
    public translate: TranslateService,
    private durationRangeService: DurationRangeService
  ) {}

  public canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean | UrlTree {
    const isLoggedIn = this.accessService.hasValidToken();
    const isAllowed = this.clientConfig.allowedRoutes.concat(internalRoutes).includes(route.url[0].path);
    const companion_app_token = localStorage.getItem('companion-app-access-token');

    if (!isLoggedIn) {
      const wildcardRouteTree = this.router.parseUrl(this.clientConfig.wildcardRoute);
      const companion_app_token = localStorage.getItem('companion-app-access-token');
      if (!companion_app_token) {
        this.accessService.clearLocalStorage();
      }
      wildcardRouteTree.queryParams = { redirectUrl: state.url };
      return wildcardRouteTree;
    }
    const isAccessible = this.isRouteAccessible(route.url[0].path);
    if (!(isAllowed && isAccessible)) {
      const lang = this.dataService._currentLanguage.value || 'en';
      this.translate
        .getTranslation(lang)
        .pipe(take(1))
        .subscribe((lang) => {
          if (!companion_app_token) {
            this.snackBarService.failure(lang['pageAccessDenied']);
          }
        });
      if (companion_app_token && companion_app_token.length > 0) {
        this.router.navigate(['notifications-page']);
      } else {
        this.router.navigate(['home']);
      }
      return false;
    }
    return true;
  }

  public isRouteAccessible(path: string) {
    const userInfo = this.accessService.getLoginInfo();
    const currentFleetId = this.dataService._currentFleet.value;
    const currentFleetInfo = (userInfo?.fleets || []).find((f) => f.fleetId === currentFleetId);

    const isInternalRoute = internalRoutes.includes(path);
    const currentPathConfig = navigationRoutes[path];

    const planDetails = this.durationRangeService?.planDetails;

    if (!currentPathConfig || !currentPathConfig.hasOwnProperty('featureFlag') || !planDetails) {
      const isAccessible =
        isInternalRoute || !currentPathConfig.uiConfigKey || currentFleetInfo.role.uiPermissions.includes(currentPathConfig.uiConfigKey);
      return isAccessible;
    }

    const currentFeatureFlag = currentPathConfig.featureFlag;
    const featureFlags = this.durationRangeService?.planDetails?.featureFlags;
    const hasFeatureFlagAccess = currentFeatureFlag && featureFlags?.includes(currentFeatureFlag);

    const isAccessible =
      isInternalRoute ||
      !currentPathConfig.uiConfigKey ||
      (currentFleetInfo.role.uiPermissions.includes(currentPathConfig.uiConfigKey) && hasFeatureFlagAccess);

    return isAccessible;
  }
}
